import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchJson } from "../utils/fetchJson";
import { buildCanonicalAuthHeaderObject } from "../utils/authenticatedRequest";
import { isExecutableHomeworkItem } from '../utils/homeDecision';
import { publicErrorMessage } from '../utils/publicError';

const initialState = {
  data: [],
  status: 'idle',
  error: null,
  ownerKey: '',
  requestId: null,
};
const EMPTY_HOMEWORK = [];

const clean = (value) => String(value || '').trim();
export const buildHomeworkTenantOwnership = (user = {}) => {
  const memberships = Array.isArray(user.memberships) ? user.memberships : [];
  const selectedSchoolCode = clean(user.selectedSchoolCode || user.schoolCode);
  const active = memberships.filter((membership) => clean(membership?.status || 'active').toLowerCase() === 'active');
  const selected = active.find((membership) => clean(membership?.schoolCode).toLowerCase() ===
    selectedSchoolCode.toLowerCase()) || (active.length === 1 ? active[0] : null);
  const schoolId = clean(user.schoolId || user.school_id || user.underBy);
  const schoolCode = clean(selected?.schoolCode || selectedSchoolCode);
  const schoolIdentity = schoolId || schoolCode;
  const userId = clean(user.userId || user.loginId || user.login_id || user.email || user._id);
  const erpStudentId = clean(user.erpId || user.ID || user.erpStudentId || user.studentId || user.student_id ||
    user.activeStudentId || user._id);
  const role = clean(selected?.role || user.role).toUpperCase();
  const membershipValid = memberships.length === 0 || Boolean(selected);
  const valid = Boolean(membershipValid && schoolIdentity && userId && erpStudentId && role === 'STUDENT');
  return {
    valid,
    schoolId,
    schoolCode,
    userId,
    erpStudentId,
    key: valid ? ['booster-homework-tenant.v2', schoolIdentity, userId, erpStudentId]
      .join('|') : '',
  };
};

export const selectOwnedHomework = (state, user) => {
  const ownership = buildHomeworkTenantOwnership(user);
  return ownership.valid && state.homework?.ownerKey === ownership.key && Array.isArray(state.homework?.data)
    ? state.homework.data : EMPTY_HOMEWORK;
};

export const fetchHomework = createAsyncThunk(
    'homework/fetchHomework',
    async ({ user, tenantOwnership }, { rejectWithValue, signal }) => {
      const ownership = tenantOwnership?.key ? tenantOwnership : buildHomeworkTenantOwnership(user);
      if (!ownership.valid) return rejectWithValue('Canonical homework tenant identity is required.');
      const baseUrl = process.env.REACT_APP_API_ADDRESS || "";
      const headers = buildCanonicalAuthHeaderObject({ "Content-type": "application/json" });

      const homeworkResponse = await fetchJson(
        `${baseUrl}/api/assignment/viewHomework/current`,
        { headers, signal },
        { cacheTtlMs: 15_000 }
      );

      if (!homeworkResponse.ok) {
        return rejectWithValue(publicErrorMessage(homeworkResponse, 'Homework is temporarily unavailable.'));
      }

      const homeworkPayload =
        homeworkResponse.data && typeof homeworkResponse.data === "object" ? homeworkResponse.data : {};
      if (!Array.isArray(homeworkPayload.data)) return rejectWithValue('Homework is temporarily unavailable.');
      // The server already projects exact canonical item completion from its
      // Practice event authority. A legacy topic total (or stale local maximum)
      // must not overwrite that result. Historical staff reporting is separate.
      return homeworkPayload.data.map(row => ({ ...row,
        assignments: (Array.isArray(row.assignments) ? row.assignments : [])
          .filter(isExecutableHomeworkItem).map(item => ({ ...item,
            topic: String(item.topic || '').replace(/\s/g, ''),
            attempted: Number(item.attempted) || 0, correct: Number(item.correct) || 0,
            completed: item.completed === true,
          })),
      })).filter(row => row.assignments.length > 0);
    }
);

export const homeworkSlice = createSlice({
    name: 'homework',
    initialState,
    reducers: {
        clearHomework: (_state, action) => {
          return { ...initialState, ownerKey: clean(action.payload?.ownerKey) }
        }
    },
    extraReducers: (builder) => {
        builder
          .addCase(fetchHomework.pending, (state, action) => {
            const ownership = action.meta.arg?.tenantOwnership?.key
              ? action.meta.arg.tenantOwnership : buildHomeworkTenantOwnership(action.meta.arg?.user);
            state.data = [];
            state.status = 'loading';
            state.error = null;
            state.ownerKey = ownership.valid ? ownership.key : '';
            state.requestId = action.meta.requestId;
          })
          .addCase(fetchHomework.fulfilled, (state, action) => {
            const ownership = action.meta.arg?.tenantOwnership?.key
              ? action.meta.arg.tenantOwnership : buildHomeworkTenantOwnership(action.meta.arg?.user);
            if (!ownership.valid || state.ownerKey !== ownership.key || state.requestId !== action.meta.requestId) return;
            state.status = 'succeeded';
            state.data = action.payload;
            state.requestId = null;
          })
          .addCase(fetchHomework.rejected, (state, action) => {
            const ownership = action.meta.arg?.tenantOwnership?.key
              ? action.meta.arg.tenantOwnership : buildHomeworkTenantOwnership(action.meta.arg?.user);
            if (state.ownerKey !== (ownership.valid ? ownership.key : '') || state.requestId !== action.meta.requestId) return;
            state.status = 'failed';
            state.error = action.payload || action.error.message;
            state.data = [];
            state.requestId = null;
          });
      },
  });

  export const { clearHomework } = homeworkSlice.actions;
  export default homeworkSlice.reducer;
